import { useCallback, useEffect, useState } from "react"
import {
  getBackgroundMusicEnabled,
  setBackgroundMusicEnabled,
} from "@/utils/backgroundMusicPreference"
import {
  DEFAULT_MUSIC_VOLUME,
  MUSIC_VOLUME_CHANGED_EVENT,
  getMusicVolume,
  setMusicVolume,
} from "@/utils/volumePreference"
import { startBackgroundMusic, stopBackgroundMusic } from "@/utils/audioManager"

/**
 * The one place the music note button (game bar and navbar) and the volume
 * slider in settings meet.
 *
 * Off and "volume 0" are treated as the same state, so the two controls can
 * never disagree: the slider reads 0 while music is off, and dragging it to 0
 * switches the button off. The *stored* level is left alone by a mute, so
 * switching music back on returns to where the slider was rather than to full.
 *
 * Both controls also stay live while the other is used — every change goes out
 * as a window event that each mounted copy of this hook picks up.
 */
export const useBackgroundMusicPreference = () => {
  const [enabled, setEnabled] = useState(getBackgroundMusicEnabled)
  const [storedVolume, setStoredVolume] = useState(getMusicVolume)

  useEffect(() => {
    const handlePreferenceChange = (event: Event) => {
      setEnabled((event as CustomEvent<boolean>).detail)
    }
    const handleVolumeChange = (event: Event) => {
      setStoredVolume((event as CustomEvent<number>).detail)
    }

    window.addEventListener(
      "background-music-preference-changed",
      handlePreferenceChange
    )
    window.addEventListener(MUSIC_VOLUME_CHANGED_EVENT, handleVolumeChange)
    return () => {
      window.removeEventListener(
        "background-music-preference-changed",
        handlePreferenceChange
      )
      window.removeEventListener(MUSIC_VOLUME_CHANGED_EVENT, handleVolumeChange)
    }
  }, [])

  const setMusicEnabled = useCallback((next: boolean) => {
    // Switching on with the level still at zero would leave the button lit and
    // the game silent, so give it something to play.
    if (next && getMusicVolume() <= 0) setMusicVolume(DEFAULT_MUSIC_VOLUME)
    setBackgroundMusicEnabled(next)
    setEnabled(next)
    if (next) startBackgroundMusic()
    else stopBackgroundMusic()
  }, [])

  const toggle = useCallback(() => {
    setMusicEnabled(!getBackgroundMusicEnabled())
  }, [setMusicEnabled])

  const setVolume = useCallback(
    (next: number) => {
      if (next <= 0) {
        setMusicEnabled(false)
        return
      }
      setMusicVolume(next)
      if (!getBackgroundMusicEnabled()) setMusicEnabled(true)
    },
    [setMusicEnabled]
  )

  return { enabled, toggle, volume: enabled ? storedVolume : 0, setVolume }
}
